import { useState, useRef, useCallback } from "react";
import { GpuListing, SortField, SortDirection } from "../types/GpuListing";
import { mockListings } from "../mocks/mockListings";

interface PaginatedResponse {
  listings: GpuListing[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  available_manufacturers: string[];
  available_retailers: string[];
  last_updated: string | null;
}

export interface FilterParams {
  manufacturers?: string[];
  retailers?: string[];
  stock?: string[];
  modelSearch?: string;
  productSearch?: string;
  sortField?: SortField;
  sortDirection?: SortDirection;
  page?: number;
}

export const useListings = (isMockMode: boolean) => {
  const [listings, setListings] = useState<GpuListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalListings, setTotalListings] = useState(0);
  const [availableManufacturers, setAvailableManufacturers] = useState<string[]>([]);
  const [availableRetailers, setAvailableRetailers] = useState<string[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const etags = useRef<Map<string, string>>(new Map());
  const currentCard = useRef<string>('');
  const currentFilters = useRef<FilterParams>({});

  const fetchListings = useCallback(async (
    cardOverride?: string,
    filters: FilterParams = {}
  ) => {
    try {
      setLoading(true);

      const gpuCard = cardOverride || localStorage.getItem('gpuCard') || '';
      currentCard.current = gpuCard;
      currentFilters.current = filters;

      if (isMockMode) {
        await new Promise(resolve => setTimeout(resolve, 500));
        const page = filters.page || 1;
        const start = (page - 1) * 10;
        setListings(mockListings.slice(start, start + 10));
        setTotalListings(mockListings.length);
        setTotalPages(Math.ceil(mockListings.length / 10));
        setCurrentPage(page);
        setAvailableManufacturers(Array.from(new Set(mockListings.map(l => l.manufacturer))).sort());
        setAvailableRetailers(Array.from(new Set(mockListings.map(l => l.retailer))).sort());
        setLastUpdated(new Date());
        setError(null);
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:2401";

      // Build query params
      const params = new URLSearchParams();
      params.set('card', gpuCard);
      params.set('page', String(filters.page || 1));
      params.set('per_page', '10');

      if (filters.manufacturers && filters.manufacturers.length > 0) {
        params.set('manufacturers', filters.manufacturers.join(','));
      }
      if (filters.retailers && filters.retailers.length > 0) {
        params.set('retailers', filters.retailers.join(','));
      }
      if (filters.stock && filters.stock.length > 0) {
        params.set('stock', filters.stock.join(','));
      }
      if (filters.modelSearch) {
        params.set('model_search', filters.modelSearch);
      }
      if (filters.productSearch) {
        params.set('product_search', filters.productSearch);
      }
      if (filters.sortField) {
        params.set('sort_field', filters.sortField);
      }
      if (filters.sortDirection) {
        params.set('sort_dir', filters.sortDirection);
      }

      const url = `${apiUrl}/api/listings?${params.toString()}`;

      // Use cached ETag for this specific query
      const headers: HeadersInit = {};
      const cachedEtag = etags.current.get(url);
      if (cachedEtag) {
        headers['If-None-Match'] = cachedEtag;
      }

      const response = await fetch(url, { headers });

      // 304 Not Modified - data hasn't changed
      if (response.status === 304) {
        setLoading(false);
        setError(null);
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to fetch listings");
      }

      // Store ETag for this specific query
      const newEtag = response.headers.get('etag');
      if (newEtag) {
        etags.current.set(url, newEtag);
      }

      const data: PaginatedResponse = await response.json();
      setListings(data.listings);
      setTotalListings(data.total);
      setTotalPages(data.total_pages);
      setCurrentPage(data.page);
      setAvailableManufacturers(data.available_manufacturers);
      setAvailableRetailers(data.available_retailers);
      setLastUpdated(data.last_updated ? new Date(data.last_updated) : null);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [isMockMode]);

  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages) {
      fetchListings(currentCard.current, { ...currentFilters.current, page });
    }
  }, [fetchListings, totalPages]);

  const refetchWithFilters = useCallback((filters: FilterParams) => {
    fetchListings(currentCard.current, { ...filters, page: 1 });
  }, [fetchListings]);

  return {
    listings,
    loading,
    error,
    currentPage,
    totalPages,
    totalListings,
    availableManufacturers,
    availableRetailers,
    lastUpdated,
    fetchListings,
    goToPage,
    refetchWithFilters,
  };
};
